import React from 'react';
import { Link } from 'react-router-dom';

const gradientBg = "bg-gradient-to-br from-[#0f2027] via-[#2c5364] to-[#1a2980]";
const glassCard = "bg-white/5 backdrop-blur-lg rounded-2xl shadow-xl border border-white/10 p-6";

const resourceLinks = [
  {
    title: "Centre d'aide Jupipotes",
    url: "/help",
    internal: true
  },
  {
    title: "La communauté, le J.U.P et le DAO",
    url: "/community-dao",
    internal: true
  },
  {
    title: "Qu'est-ce que le J.U.P ?",
    url: "/about-jup",
    internal: true
  }
];

const communityLinks = [
  {
    title: "Obtenir de l'aide (Support)",
    url: "/support",
    internal: true
  },
  {
    title: "Messages & Discussions",
    url: "/messages",
    internal: true
  },
  {
    title: "Explorer les Profils",
    url: "/",
    internal: true
  }
];

const legalLinks = [
  {
    title: "Politique de confidentialité",
    url: "/privacy-policy",
    internal: true
  },
  {
    title: "Conditions d'utilisation",
    url: "/terms",
    internal: true
  }
];

const groupInfos = [
  {
    name: "Utilisateurs",
    desc: "Membres de la communauté Jupiter.",
    icon: "👤"
  },
  {
    name: "Équipe",
    desc: "Développeurs et contributeurs principaux.",
    icon: "🛠️"
  },
  {
    name: "DAO",
    desc: "Organisation autonome décentralisée.",
    icon: "🪐"
  },
  {
    name: "Catdets",
    desc: "Membres actifs et ambassadeurs.",
    icon: "🚀"
  },
  {
    name: "Working Groups",
    desc: "Groupes de travail spécialisés.",
    icon: "🤝"
  }
];

const JUPResources: React.FC = () => {
  return (
    <div className={`min-h-screen w-full ${gradientBg} flex flex-col items-center justify-start py-8 px-2 md:px-0`}>
      <div className="w-full max-w-4xl mx-auto space-y-10">
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12">
          <img src="https://support.jup.ag/hc/article_attachments/20007956605852" alt="JUP Diagram" className="w-full md:w-1/2 rounded-2xl shadow-2xl border-2 border-cyan-400" />
          <div className={`${glassCard} flex-1 animate-fade-in-up`}>
            <h1 className="text-4xl md:text-5xl font-extrabold text-emerald-100 mb-4 tracking-tight drop-shadow-lg">Ressources Jupiter United Planet (J.U.P)</h1>
            <h2 className="text-2xl font-semibold text-cyan-300 mb-2">Présentation du J.U.P</h2>
            <p className="text-emerald-200 mb-4 text-lg">
              Le <strong>J.U.P</strong> est la vision de Jupipotes pour une communauté dynamique et synergique, composée de plusieurs groupes : Utilisateurs, Équipe, DAO, Catdets et Working Groups. Ensemble, ils font avancer Jupipotes et l'écosystème Web3.
            </p>
            <Link to="/about-jup" className="inline-block px-4 py-2 rounded-lg bg-cyan-400/20 text-cyan-300 font-semibold hover:bg-cyan-400/40 transition">
              En savoir plus sur le J.U.P
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <section className={`${glassCard} animate-fade-in-up`}>
            <h2 className="text-xl font-bold text-cyan-300 mb-4 flex items-center gap-2">
              <svg className="h-6 w-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 17l4 4 4-4m-4-5v9" /></svg>
              Guides & FAQ
            </h2>
            <ul className="space-y-3">
              {resourceLinks.map((link, idx) => (
                <li key={idx}>
                  <Link to={link.url} className="flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition font-medium">
                    <span className="inline-block w-2 h-2 bg-cyan-400 rounded-full" />
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
          <section className={`${glassCard} animate-fade-in-up`}>
            <h2 className="text-xl font-bold text-cyan-300 mb-4 flex items-center gap-2">
              <svg className="h-6 w-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-1.414 1.414A9 9 0 105.636 18.364l1.414-1.414" /></svg>
              Communauté & Support
            </h2>
            <ul className="space-y-3">
              {communityLinks.map((link, idx) => (
                <li key={idx}>
                  <Link to={link.url} className="flex items-center gap-2 text-emerald-300 hover:text-emerald-200 transition font-medium">
                    <span className="inline-block w-2 h-2 bg-emerald-400 rounded-full" />
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <section className={`${glassCard} animate-fade-in-up`}>
          <h2 className="text-xl font-bold text-cyan-300 mb-4 flex items-center gap-2">
            <svg className="h-6 w-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Liens officiels & légaux
          </h2>
          <ul className="flex flex-col md:flex-row gap-4">
            {legalLinks.map((link, idx) => (
              <li key={idx}>
                <Link to={link.url} className="flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition font-medium">
                  <span className="inline-block w-2 h-2 bg-cyan-400 rounded-full" />
                  {link.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className={`${glassCard} animate-fade-in-up`}>
          <h2 className="text-xl font-bold text-cyan-300 mb-6 flex items-center gap-2">
            <svg className="h-6 w-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Découvrir les groupes du J.U.P
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {groupInfos.map((group, idx) => (
              <div key={idx} className="flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-br from-cyan-900/40 via-emerald-900/30 to-cyan-800/40 shadow-lg border border-cyan-700/30">
                <span className="text-4xl mb-2 drop-shadow-lg">{group.icon}</span>
                <span className="text-lg font-bold text-cyan-200 mb-1">{group.name}</span>
                <span className="text-emerald-200 text-center text-sm">{group.desc}</span>
              </div>
            ))}
          </div>
        </section>

        <div className="flex justify-center mt-8">
          <Link to="/JUPResources" className="flex items-center px-6 py-3 rounded-xl bg-cyan-400/20 text-cyan-200 font-bold shadow-lg hover:bg-cyan-400/40 transition">
            <svg className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 20h9" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4h9" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h.01" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 9h16" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 15h16" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 20h.01" />
            </svg>
            <span className="ml-2">Ressources JUP</span>
          </Link>
        </div>
      </div>
      {/* Animations */}
      <style>{`
        .animate-fade-in-up {
          animation: fadeInUp 0.8s cubic-bezier(.39,.575,.565,1) both;
        }
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(40px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default JUPResources;
